// src/pages/ContactDetailPage.js — Detail view for a single contact
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { CreateTaskModal } from './TasksPage'; // [marker: invoice-links v1]
import EmailComposer from '../components/EmailComposer'; // [marker: email-compose v1]
import { ContactFormModal } from './ContactsPage';
import { TagBadge } from './ContactTagsSettingsPage';
import { MergeWithPicker } from '../components/ContactMerge'; // [marker: contact-merge v1]

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManagerOrAbove } = useAuth();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [addingTask, setAddingTask] = useState(false); // [marker: invoice-links v1]
  const [merging, setMerging] = useState(false); // [marker: contact-merge v1]

  useEffect(() => { loadContact(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    api.get(`/invoices?contact_id=${id}&limit=100`)
      .then(res => setInvoices(res.data?.data || res.data || []))
      .catch(() => setInvoices([]));
  }, [id]);

  const loadContact = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/contacts/${id}`);
      setContact(res.data);
    } catch (err) {
      toast.error('Failed to load contact.');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete contact "${contact.first_name} ${contact.last_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Contact deleted.');
      navigate('/contacts');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete contact.');
    }
  };

  const handleSaved = () => {
    setShowEdit(false);
    loadContact();
    toast.success('Contact updated.');
  };


  // [marker: email-compose v1] review / edit the invite before it goes out
  const handleSendPortalInvite = () => {
    if (!contact.email) return toast.error('This contact has no email address.');
    setSendingInvite(true);
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading…</div>;
  const inviteComposer = sendingInvite && contact ? (
    <EmailComposer
      kind="portal_invite" id={contact.id}
      title={contact.portal_last_login_at ? 'Resend Portal Invite' : 'Send Portal Invite'}
      sendLabel="Send Invite"
      onClose={() => setSendingInvite(false)}
      onSent={() => setSendingInvite(false)}
    />
  ) : null;
  if (!contact) return null;

  const fullName = `${contact.first_name} ${contact.last_name}`;
  const isStandalone = !contact.business_id;

  return (
    <div>
      {inviteComposer}
      <div style={{ marginBottom: 16 }}>
        <Link to="/contacts" style={{ fontSize: '0.875rem', color: 'var(--primary)' }}>← Back to Contacts</Link>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ marginBottom: 4 }}>{fullName}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {contact.title && <span style={{ color: 'var(--gray-500)' }}>{contact.title}</span>}
            {contact.contact_type && (
              <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                {contact.contact_type.replace(/_/g, ' ')}
              </span>
            )}
            {isStandalone && (
              <span className="badge badge-warning">Standalone</span>
            )}
            {contact.email_unsubscribed && (
              <span className="badge badge-danger">Unsubscribed</span>
            )}
            {contact.email_error && (
              <span className="badge badge-danger" title="This email bounced. The contact is excluded from email blasts.">Email Error</span>
            )}
          </div>
        </div>
        {isManagerOrAbove() && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {contact.email && contact.business_id && (
              <button
                className="btn btn-secondary"
                onClick={handleSendPortalInvite}
                disabled={sendingInvite}
                title={contact.portal_last_login_at ? 'Resend portal invite (acts as password reset)' : 'Send first-time portal invite'}
              >
                ✉ {contact.portal_last_login_at ? 'Resend Portal Invite' : 'Send Portal Invite'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => setMerging(true)}>Merge with…</button>
            <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>Edit</button>
            <button className="btn btn-ghost"     onClick={handleDelete}>Delete</button>
          </div>
        )}
      </div>

      {/* Tags */}
      <TagsSection contact={contact} onChange={loadContact} canEdit={isManagerOrAbove()} />

      {/* Info card */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Contact Info</span></div>
          <div className="card-body">
            <InfoRow label="Company"        value={contact.company_name} />
            <InfoRow label="Email"          value={contact.email} />
            {/* [marker: email-bounce-detect v1] */}
            <InfoRow label="Email Error"    value={contact.email_error
              ? 'X' + (contact.email_error_at ? ' — bounced ' + fmtDate(contact.email_error_at) : '') + ' (excluded from blasts)'
              : null} />
            {/* [marker: contact-secondary-email v1] shows only when present */}
            <InfoRow label="Secondary Email" value={contact.secondary_email} />
            <InfoRow label="Phone"          value={contact.phone} />
            {/* [marker: contact-extra-fields v1] */}
            <InfoRow label="Secondary Phone" value={contact.secondary_phone} />
            <InfoRow label="VIP"            value={contact.vip} />
            <InfoRow label="Association"    value={contact.association} />
            <InfoRow label="Birthday"       value={contact.birthday ? fmtDate(contact.birthday) : null} />
            <InfoRow label="Email Frequency" value={contact.contact_frequency?.replace(/_/g, ' ')} />
            <InfoRow label="Email Opt-In"   value={contact.email_blast_opt_in ? 'Yes' : 'No'} />
            <InfoRow label="Last Contacted" value={contact.last_contacted_at ? fmtDate(contact.last_contacted_at) : 'Never'} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">{isStandalone ? 'Affiliation' : 'Business & Role'}</span>
          </div>
          <div className="card-body">
            {contact.business_id ? (
              <>
                <InfoRow
                  label="Business"
                  value={
                    <Link to={`/members/${contact.business_id}`}>
                      {contact.business_name}
                    </Link>
                  }
                />
                <InfoRow label="Status"         value={contact.business_status} />
                <InfoRow label="Membership"     value={contact.membership_type_name} />
                <InfoRow label="Portal Role"    value={contact.portal_role?.replace(/_/g, ' ')} />
                <InfoRow label="Portal Login"   value={contact.portal_last_login_at ? fmtDate(contact.portal_last_login_at) : 'Never'} />
              </>
            ) : (
              <>
                <InfoRow label="Office / Org"     value={contact.office_or_org} />
                <InfoRow label="Government Level" value={contact.government_level} />
                {!contact.office_or_org && !contact.government_level && (
                  <p style={{ color: 'var(--gray-500)', fontStyle: 'italic', margin: 0 }}>
                    No affiliation info — standalone contact.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      {(contact.address_line1 || contact.city || contact.state) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Personal Address</span></div>
          <div className="card-body">
            {contact.address_line1 && <div>{contact.address_line1}</div>}
            {contact.address_line2 && <div>{contact.address_line2}</div>}
            <div>
              {[contact.city, contact.state, contact.zip_code].filter(Boolean).join(', ')}
            </div>
          </div>
        </div>
      )}

      {/* Event Attendance History (per user's preference: after info, before notes) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Event Attendance</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {contact.eventHistory?.length || 0} event{contact.eventHistory?.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="card-body" style={{ padding: contact.eventHistory?.length ? 0 : undefined }}>
          {!contact.eventHistory || contact.eventHistory.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No events attended yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date</th>
                  <th>Ticket #</th>
                  <th>Checked In</th>
                </tr>
              </thead>
              <tbody>
                {contact.eventHistory.map(ev => (
                  <tr key={ev.ticket_id}>
                    <td>
                      <Link to={`/events/${ev.event_id}`}>{ev.event_name}</Link>
                    </td>
                    <td>{fmtDate(ev.event_date)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{ev.ticket_number}</td>
                    <td>
                      {ev.checked_in ? (
                        <span className="badge badge-success">Yes</span>
                      ) : (
                        <span style={{ color: 'var(--gray-400)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Invoices</span>
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
              {invoices?.length || 0} invoice{invoices?.length === 1 ? '' : 's'}
            </span>
            {isManagerOrAbove() && (
              <Link className="btn btn-primary btn-sm"
                    to={`/invoices/new?contactId=${contact.id}${contact.business_id ? `&businessId=${contact.business_id}` : ''}`}>+ Create Invoice</Link>
            )}
          </span>
        </div>
        <div className="card-body" style={{ padding: invoices?.length ? 0 : undefined }}>
          {invoices === null ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>Loading…</p>
          ) : invoices.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No invoices yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td><Link to={`/invoices/${inv.id}`} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{inv.invoice_number}</Link></td>
                    <td style={{ fontSize: '0.85rem' }}>{inv.invoice_type}</td>
                    <td>${parseFloat(inv.total_amount || 0).toFixed(2)}</td>
                    <td><span className={`badge badge-${inv.status}`}>{inv.status}</span></td>
                    <td>{inv.due_date ? fmtDate(inv.due_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Sponsorships — [marker: invoice-links v1] */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Sponsorships</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{(contact.sponsorships || []).length}</span>
        </div>
        <div className="card-body" style={{ padding: (contact.sponsorships || []).length ? 0 : undefined }}>
          {!(contact.sponsorships || []).length ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No sponsorships.</p>
          ) : (
            <table className="data-table">
              <thead><tr><th>Event</th><th>Level</th><th>Amount</th><th>Status</th><th>Invoice</th></tr></thead>
              <tbody>
                {contact.sponsorships.map(sp => (
                  <tr key={sp.id}>
                    <td>{sp.event_id ? <Link to={`/events/${sp.event_id}`}>{sp.event_name}</Link> : '—'}</td>
                    <td>{sp.tier_name || '—'}</td>
                    <td>${parseFloat(sp.amount || 0).toFixed(2)}</td>
                    <td><span className={`badge badge-${sp.payment_status}`}>{String(sp.payment_status || '').replace('_', ' ')}</span></td>
                    <td>{sp.invoice_id ? <Link to={`/invoices/${sp.invoice_id}`} style={{ fontFamily: 'monospace' }}>{sp.invoice_number}</Link> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Communication log — three separate sections */}
      <CommunicationLog communications={contact.communications} contactId={contact.id} />

      {/* Linked tasks */}
      <LinkedTasksSection tasks={contact.linkedTasks} onAdd={() => setAddingTask(true)} />
      {addingTask && (
        <CreateTaskModal
          initialLink={{ type: 'contact', id: contact.id, name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() }}
          onClose={() => setAddingTask(false)}
          onSaved={() => { setAddingTask(false); loadContact(); }}
        />
      )}

      {merging && (
        <MergeWithPicker contact={contact} onClose={() => setMerging(false)}
          onMerged={(keptId) => { setMerging(false); if (String(keptId) === String(contact.id)) loadContact(); else navigate(`/contacts/${keptId}`); }} />
      )}

      {showEdit && (
        <ContactFormModal
          contact={contact}
          onClose={() => setShowEdit(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ─── TagsSection ─────────────────────────────────────────────────────────────
function TagsSection({ contact, onChange, canEdit }) {
  const [allTags, setAllTags] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/contact-tags').then(res => setAllTags(res.data)).catch(() => {});
  }, []);

  const contactTagIds = new Set((contact.tags || []).map(t => t.id));
  const availableToAdd = allTags.filter(t => !contactTagIds.has(t.id));

  const addTag = async (tagId) => {
    setLoading(true);
    try {
      await api.post(`/contacts/${contact.id}/tags`, { tagId });
      toast.success('Tag applied.');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to apply tag.');
    } finally {
      setLoading(false);
      setShowPicker(false);
    }
  };

  const removeTag = async (tag) => {
    if (tag.is_auto) {
      if (!window.confirm(`"${tag.name}" is an auto-applied tag. Remove anyway?`)) return;
    }
    setLoading(true);
    try {
      await api.delete(`/contacts/${contact.id}/tags/${tag.id}`);
      toast.success('Tag removed.');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove tag.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <span className="card-title">Tags</span>
        {canEdit && availableToAdd.length > 0 && (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPicker(s => !s)} disabled={loading}>
            + Add Tag
          </button>
        )}
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(contact.tags || []).length === 0 ? (
          <p style={{ color: 'var(--gray-500)', margin: 0 }}>No tags yet.</p>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {contact.tags.map(tag => (
              <TagBadge
                key={tag.id}
                tag={tag}
                onRemove={canEdit ? () => removeTag(tag) : null}
              />
            ))}
          </div>
        )}

        {showPicker && availableToAdd.length > 0 && (
          <div style={{
            padding: 12,
            background: 'var(--gray-50)',
            borderRadius: 6,
            border: '1px solid var(--gray-200)',
          }}>
            <div style={{ fontSize: '0.85rem', marginBottom: 8, color: 'var(--gray-600)' }}>
              Click a tag to apply:
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {availableToAdd.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => addTag(tag.id)}
                  disabled={loading}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <TagBadge tag={tag} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LinkedTasksSection ──────────────────────────────────────────────────────
function LinkedTasksSection({ tasks, onAdd }) {
  if (!tasks) tasks = [];
  const PRIORITY_STYLES = {
    urgent: { color: '#dc2626', bg: '#fef2f2' },
    high:   { color: '#d97706', bg: '#fffbeb' },
    normal: { color: '#1a3a5c', bg: '#f0f4f8' },
    low:    { color: '#9ca3af', bg: '#f9fafb' },
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <span className="card-title">Linked Tasks</span>
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {tasks.length} task{tasks.length === 1 ? '' : 's'}
          </span>
          {onAdd && <button className="btn btn-primary btn-sm" onClick={onAdd}>+ Add Task</button>}
        </span>
      </div>
      <div className="card-body" style={{ padding: tasks.length ? 0 : undefined }}>
        {tasks.length === 0 ? (
          <p style={{ color: 'var(--gray-500)', margin: 0 }}>
            No tasks linked to this contact yet. Click “+ Add Task” to add one.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Due</th>
                <th>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => {
                const isSnoozed = t.snoozed_until && new Date(t.snoozed_until) > new Date();
                const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'complete';
                const ps = PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal;
                return (
                  <tr key={t.id} style={{ opacity: t.status === 'complete' ? 0.5 : (isSnoozed ? 0.6 : 1) }}>
                    <td>
                      <Link to={`/tasks`} state={{ taskId: t.id }} style={{ fontWeight: 600 }}>
                        {t.title}
                      </Link>
                      {isSnoozed && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#7c3aed' }}>💤</span>}
                    </td>
                    <td>
                      <span style={{ fontSize: '0.7rem', background: ps.bg, color: ps.color, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {t.priority}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{ fontSize: '0.7rem' }}>
                        {t.status?.replace('_',' ')}
                      </span>
                    </td>
                    <td style={{ color: isOverdue ? 'var(--danger)' : undefined, fontSize: '0.85rem' }}>
                      {t.due_date ? fmtDate(t.due_date) : '—'}
                    </td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                      {t.assigned_to_first_name ? `${t.assigned_to_first_name} ${t.assigned_to_last_name}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── CommunicationLog ────────────────────────────────────────────────────────
function CommunicationLog({ communications, contactId }) {
  const comm = communications || { automatedEmails: [], blasts: [], letters: [] };
  const totalCount = (comm.automatedEmails?.length || 0)
                    + (comm.blasts?.length || 0)
                    + (comm.letters?.length || 0);

  return (
    <>
      <h2 style={{ marginTop: 28, marginBottom: 12, fontSize: '1.25rem' }}>
        Communication Log
        <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--gray-500)', marginLeft: 8 }}>
          ({totalCount} total)
        </span>
      </h2>

      {/* Automated transactional emails */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Automated Emails</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {comm.automatedEmails?.length || 0} sent
          </span>
        </div>
        <div className="card-body" style={{ padding: comm.automatedEmails?.length ? 0 : undefined }}>
          {!comm.automatedEmails?.length ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No automated emails sent yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {comm.automatedEmails.map(e => (
                  <tr key={e.id} title={e.error_message || ''}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDateTime(e.sent_at || e.scheduled_for || e.created_at)}
                    </td>
                    <td>{e.subject || <span style={{ color: 'var(--gray-400)' }}>—</span>}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>
                      {humanize(e.template_key)}
                    </td>
                    <td>
                      <StatusBadge status={e.status} />
                      {e.error_message && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--danger, #b00)', marginTop: 2, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Email blasts */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Email Blasts</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {comm.blasts?.length || 0} received
          </span>
        </div>
        <div className="card-body" style={{ padding: comm.blasts?.length ? 0 : undefined }}>
          {!comm.blasts?.length ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No email blasts sent to this contact yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subject</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {comm.blasts.map(b => (
                  <tr key={b.recipient_id}
                      onClick={() => { window.location.hash = `blast-${b.blast_id}`; }}
                      style={{ cursor: 'pointer' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDateTime(b.sent_at || b.blast_sent_at)}
                    </td>
                    <td>
                      <Link to={`/email`} state={{ blastId: b.blast_id }}
                            style={{ color: 'inherit' }}
                            onClick={e => e.stopPropagation()}>
                        {b.subject}
                      </Link>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {b.sent_at && <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>Sent</span>}
                        {b.opened_at && <span className="badge badge-info" style={{ fontSize: '0.65rem' }}>Opened</span>}
                        {b.bounced && <span className="badge badge-danger" style={{ fontSize: '0.65rem' }}>Bounced</span>}
                        {!b.sent_at && !b.bounced && <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>Pending</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Letters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Letters</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {comm.letters?.length || 0} on record
          </span>
        </div>
        <div className="card-body" style={{ padding: comm.letters?.length ? 0 : undefined }}>
          {!comm.letters?.length ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No letters on record for this contact.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {comm.letters.map(l => (
                  <tr key={l.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDateTime(l.sent_at || l.created_at)}
                    </td>
                    <td>
                      <Link to={`/letters`} state={{ letterId: l.id }}>
                        {l.subject || <span style={{ color: 'var(--gray-400)' }}>(no subject)</span>}
                      </Link>
                      {l.ai_generated && (
                        <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--gray-400)' }}>AI-generated</span>
                      )}
                    </td>
                    <td><StatusBadge status={l.status} /></td>
                    <td>
                      {l.pdf_filename && (
                        <a href={`/api/letters/${l.id}/pdf`} target="_blank" rel="noreferrer"
                           className="btn btn-secondary btn-sm">PDF</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (!status) return <span style={{ color: 'var(--gray-400)' }}>—</span>;
  const map = {
    sent:            { cls: 'badge-success', label: 'Sent' },
    delivered:       { cls: 'badge-success', label: 'Delivered' },
    finalized:       { cls: 'badge-success', label: 'Finalized' },
    scheduled:       { cls: 'badge-info',    label: 'Scheduled' },
    sending:         { cls: 'badge-info',    label: 'Sending' },
    draft:           { cls: 'badge-muted',   label: 'Draft' },
    failed:          { cls: 'badge-danger',  label: 'Failed' },
    bounced:         { cls: 'badge-danger',  label: 'Bounced' },
    cancelled:       { cls: 'badge-muted',   label: 'Cancelled' },
    skipped_holiday: { cls: 'badge-muted',   label: 'Skipped (holiday)' },
  };
  const m = map[status] || { cls: 'badge-info', label: status };
  return <span className={`badge ${m.cls}`} style={{ fontSize: '0.7rem' }}>{m.label}</span>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--gray-500)', width: 140, flexShrink: 0, fontWeight: 500, textTransform: 'capitalize' }}>{label}</span>
      <span style={{ color: 'var(--gray-900)' }}>{value}</span>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function humanize(s) {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
